/**
 * TailwindColorSelectorPopover - Searchable Tailwind CSS color palette selector
 *
 * Features:
 * - Two view modes: List (searchable) and Palette (grid)
 * - View preference stored in localStorage
 * - Auto-close on color selection
 * - Check indicator for currently selected color
 */

import { useState, useEffect, useCallback, useMemo } from "react";

import { Check, LayoutGrid, List, Search } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Hint } from "@/components/ui/hint";
import { cn } from "@/lib/utils";

import {
  TAILWIND_PALETTE,
  TAILWIND_SHADES,
  TAILWIND_COLOR_ORDER,
  formatColorFamilyName,
} from "../utils/tailwind-colors";

const STORAGE_KEY = "tailwind-color-selector-view";

interface TailwindColorSelectorPopoverProps {
  value?: string;
  onSelect: (oklchValue: string) => void;
}

/**
 * TailwindCSS logo icon
 */
function TailwindIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="currentColor"
      className={className}
      aria-hidden="true"
    >
      <path d="M12.001 4.8c-3.2 0-5.2 1.6-6 4.8 1.2-1.6 2.6-2.2 4.2-1.8.913.228 1.565.89 2.288 1.624C13.666 10.618 15.027 12 18.001 12c3.2 0 5.2-1.6 6-4.8-1.2 1.6-2.6 2.2-4.2 1.8-.913-.228-1.565-.89-2.288-1.624C16.337 6.182 14.976 4.8 12.001 4.8zm-6 7.2c-3.2 0-5.2 1.6-6 4.8 1.2-1.6 2.6-2.2 4.2-1.8.913.228 1.565.89 2.288 1.624 1.177 1.194 2.538 2.576 5.512 2.576 3.2 0 5.2-1.6 6-4.8-1.2 1.6-2.6 2.2-4.2 1.8-.913-.228-1.565-.89-2.288-1.624C10.337 13.382 8.976 12 6.001 12z" />
    </svg>
  );
}

/**
 * Color swatch button for palette grid view
 */
interface ColorSwatchButtonProps {
  color: string;
  name: string;
  isSelected: boolean;
  onClick: () => void;
  className?: string;
}

function ColorSwatchButton({
  color,
  name,
  isSelected,
  onClick,
  className,
}: ColorSwatchButtonProps) {
  return (
    <button
      type="button"
      aria-label={`Select ${name}`}
      title={name}
      onClick={onClick}
      className={cn(
        "group relative size-6 cursor-pointer rounded border transition-all",
        "hover:z-10 hover:scale-110 hover:shadow-lg",
        isSelected && "ring-2 ring-primary ring-offset-1",
        className,
      )}
      style={{ backgroundColor: color }}
    >
      {isSelected && (
        <Check
          className={cn(
            "absolute inset-0 m-auto size-3",
            // Use white check for dark colors, dark check for light colors
            "text-white mix-blend-difference",
          )}
        />
      )}
    </button>
  );
}

export function TailwindColorSelectorPopover({
  value,
  onSelect,
}: TailwindColorSelectorPopoverProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [viewMode, setViewMode] = useState<"list" | "palette">(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    return (stored as "list" | "palette") || "list";
  });

  // Persist view mode preference
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, viewMode);
  }, [viewMode]);

  // Check if a color is currently selected
  const isColorSelected = useCallback(
    (color: string) => {
      if (!value) return false;
      return value === color;
    },
    [value],
  );

  // Handle color selection
  const handleSelect = useCallback(
    (color: string) => {
      onSelect(color);
      setOpen(false);
      setSearch("");
    },
    [onSelect],
  );

  // Filter colors based on search
  const filteredColors = useMemo(() => {
    if (!search.trim()) {
      return TAILWIND_COLOR_ORDER;
    }
    const searchLower = search.toLowerCase();
    return TAILWIND_COLOR_ORDER.filter((family) =>
      family.includes(searchLower),
    );
  }, [search]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <Hint text="Tailwind Colors" delayDuration={0}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            size="icon"
            className="h-9 w-9 shrink-0"
            aria-label="Open Tailwind color palette"
          >
            <TailwindIcon className="h-4 w-4" />
          </Button>
        </PopoverTrigger>
      </Hint>

      <PopoverContent
        className="w-80 p-0 border-0 bg-popover dark:bg-zinc-900 z-[9999]"
        align="start"
        sideOffset={4}
        onWheel={(e) => e.stopPropagation()}
        onTouchMove={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-border px-3 py-2">
          <div className="flex items-center gap-2">
            <TailwindIcon className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm font-medium">Tailwind v4</span>
          </div>
          <div className="flex gap-1">
            <Hint text="List view" side="bottom" delayDuration={0}>
              <Button
                variant={viewMode === "list" ? "secondary" : "ghost"}
                size="icon"
                className="h-7 w-7"
                onClick={() => setViewMode("list")}
              >
                <List className="h-4 w-4" />
              </Button>
            </Hint>
            <Hint text="Palette view" side="bottom" delayDuration={0}>
              <Button
                variant={viewMode === "palette" ? "secondary" : "ghost"}
                size="icon"
                className="h-7 w-7"
                onClick={() => setViewMode("palette")}
              >
                <LayoutGrid className="h-4 w-4" />
              </Button>
            </Hint>
          </div>
        </div>

        {/* List View */}
        {viewMode === "list" && (
          <div className="flex flex-col">
            {/* Search Input */}
            <div className="flex items-center gap-2 border-b border-border px-3 py-2.5">
              <Search className="h-4 w-4 shrink-0 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search colors..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="flex-1 bg-transparent text-sm text-foreground placeholder:text-muted-foreground outline-none"
              />
            </div>

            {/* Color List */}
            <ScrollArea className="h-72">
              {filteredColors.length === 0 ? (
                <div className="py-6 text-center text-sm text-muted-foreground">
                  No colors found.
                </div>
              ) : (
                filteredColors.map((family) => (
                  <div key={family} className="p-1">
                    {/* Group Heading */}
                    <div className="px-2 py-1.5 text-xs font-medium text-muted-foreground">
                      {formatColorFamilyName(family)}
                    </div>
                    {/* Color Items */}
                    {TAILWIND_SHADES.map((shade) => {
                      const color = TAILWIND_PALETTE[family][shade];
                      const name = `${family}-${shade}`;
                      const isSelected = isColorSelected(color);

                      return (
                        <button
                          key={name}
                          type="button"
                          onClick={() => handleSelect(color)}
                          className={cn(
                            "relative flex w-full cursor-pointer select-none items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-none",
                            "hover:bg-accent hover:text-accent-foreground",
                            isSelected && "bg-accent",
                          )}
                        >
                          <div
                            className="size-5 rounded border"
                            style={{ backgroundColor: color }}
                          />
                          <span className="flex-1 text-left">{name}</span>
                          {isSelected && (
                            <Check className="h-4 w-4 text-primary" />
                          )}
                        </button>
                      );
                    })}
                  </div>
                ))
              )}
            </ScrollArea>
          </div>
        )}

        {/* Palette View */}
        {viewMode === "palette" && (
          <ScrollArea className="h-80 p-2">
            <div className="flex flex-col gap-1">
              {TAILWIND_COLOR_ORDER.map((family) => (
                <div key={family} className="flex gap-0.5">
                  {TAILWIND_SHADES.map((shade) => {
                    const color = TAILWIND_PALETTE[family][shade];
                    const name = `${family}-${shade}`;
                    return (
                      <ColorSwatchButton
                        key={name}
                        color={color}
                        name={name}
                        isSelected={isColorSelected(color)}
                        onClick={() => handleSelect(color)}
                        className="rounded-none first:rounded-l last:rounded-r"
                      />
                    );
                  })}
                </div>
              ))}
            </div>
          </ScrollArea>
        )}
      </PopoverContent>
    </Popover>
  );
}
